import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useModules } from '../contexts/ModuleContext';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import DashBoard from '../screens/DashBoard';
import Chat from '../screens/Chat';
import MessageSchedule from '../screens/MessageSchedule';
import LabelPage from '../screens/LabelScreen';
import CRMPage from '../screens/CRMPage';
import OpportunityDossierPage from '../screens/OpportunityDossierPage';
import SectorsPage from '../screens/SectorsPage';
import GoogleAdsLeadsPage from '../screens/GoogleAdsLeadsPage';
import AttendancePage from '../screens/AttendancePage';
import ProfilePage from '../screens/ProfilePage';
import AIAgentPage from '../screens/AIAgentPage';
import FollowUpPage from '../screens/FollowUpPage';
import GroupsPage from '../screens/GroupsPage';
import ContactsPage from '../screens/ContactsPage';
import BroadcastPage from '../screens/BroadcastPage';
import EmailPage from '../screens/EmailPage';
import AgendaPage from '../screens/AgendaPage';
import { FiUsers, FiUser, FiTarget, FiCalendar, FiLayers, FiTag, FiMail, FiCpu, FiMic, FiHome, FiMessageSquare, FiBriefcase, FiSettings, FiTrendingUp } from 'react-icons/fi';
import SessionService from '../services/SessionService';
import { clearAppCache } from '../services/BootstrapService';
import { getUser } from '../services/UserService';
import { getSectors, Sector, getWhatsAppStatus } from '../services/SectorService';
import { cloudWhatsAppConnectionState, getMetaConnectionStatus } from '../services/MetaConnectionService';
import { useMenu } from '../contexts/MenuContext';
import { useSector } from '../contexts/SectorContext';
import { getContacts, getContactsWithFilters, getContactCounts } from '../services/ContactService';
import '../styles/CombinedMenu/CombinedMenu.css';
import ErrorBoundary from './ErrorBoundary';
import '../mendsAi/styles/mendsAi.css';
import ChatWebSocketService from '../services/ChatWebSocketService';
import { processAvatarUrl } from '../utils/avatarUtils';
import { userInitials } from '../utils/userInitials';
import { config } from '../config';
import logo from '../assets/logo-horizontal.png';
import { LoadingSpinner } from './LoadingSpinner';
import NotificationBell from './NotificationBell';
import notificationService, { Notification } from '../services/NotificationService';
import { dedupeChatContactsForList, mergeChatListFirstPageFromPoll } from '../utils/formatters';
import { subscribeCentralQuestion } from '../mendsAi/lib/centralQuestion';
import {
    CommandPalette,
    useCommandPaletteHotkey,
    ApprovalsPage,
    OpportunitiesPage,
    IntelligenceCenterPage,
    MindPage,
    TodayPage,
    RegisterPage,
    OnboardingPage,
    isMendsVisionEnabled,
    RegisterConversationModal,
} from '../mendsAi';
import { subscribeRegisterConversation, openRegisterConversation, RegisterConversationOpenDetail } from '../mendsAi/openRegister';
import RegisterFab from '../mendsAi/components/RegisterFab';

/** Labels menu hidden visually for now — keep route/page intact. */
const SHOW_LABELS_MENU = false;

/** Páginas que, com a MENDS ligada, ficam dentro de Configurações (abre o grupo ao visitá-las). */
const SETTINGS_CHILD_KEYS = new Set(['1', '15', '12', '14', '3', '26', '22', '28', '10', '27', '29', '30']);

interface ProfileUpdateEvent extends CustomEvent {
    detail: {
        avatarUrl: string | null;
        name: string;
        timestamp: number;
    };
}

interface UnreadMessagesState {
    [contactId: number]: boolean;
}

const PROFILE_UPDATED_EVENT = 'profileUpdated';
const CONTACTS_POLL_INTERVAL = 5000; // 5 seconds
const CONNECTION_LABEL_MAX_LEN = 44;

function truncateConnectionLabel(label: string, maxLen = CONNECTION_LABEL_MAX_LEN): string {
    if (!label) return '';
    if (label.length <= maxLen) return label;
    return `${label.slice(0, Math.max(1, maxLen - 1))}…`;
}

const CombinedMenu: React.FC = () => {
    // Context hooks
    const { sectors, selectedSectorId, selectSector, isLoading: isSectorsLoading, isOfficial } = useSector();
    const { modules, refreshModules } = useModules();
    const selectedSectorIdRef = useRef(selectedSectorId);

    // Update ref when sector changes
    useEffect(() => {
        selectedSectorIdRef.current = selectedSectorId;
    }, [selectedSectorId]);

    // Local UI state
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState<string | null>(null);
    const [selectedMenuKey, setSelectedMenuKey] = useState<string>(''); // a URL define a chave logo no primeiro efeito
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [expandedSubmenus, setExpandedSubmenus] = useState<string[]>([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [websocketConnectionStatus, setWebsocketConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    const [whatsappConnectionStatus, setWhatsappConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    const userDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const pathnameRef = useRef(location.pathname);
    const { drawerVisible, setDrawerVisible, setDrawerType } = useMenu();

    useEffect(() => {
        pathnameRef.current = location.pathname;
    }, [location.pathname]);
    const navigationInProgressRef = useRef(false);
    /** Per-contact badges inside Chat (real-time incoming while another thread is open). */
    const [chatNewMessagesState, setChatNewMessagesState] = useState<UnreadMessagesState>({});
    /** Sidebar dot on Chat menu — only when away from /chat and a new message arrives. */
    const [chatMenuHasNewActivity, setChatMenuHasNewActivity] = useState(false);
    const [contacts, setContacts] = useState<any[]>([]);
    const [totalContacts, setTotalContacts] = useState(0);
    const [hasMoreContacts, setHasMoreContacts] = useState(false);
    const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
    const [archivedCount, setArchivedCount] = useState(0);
    const [favoritesCount, setFavoritesCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [whatsappGroupsCount, setWhatsappGroupsCount] = useState(0);
    /** Next offset for /contacts/list (fixed page size; append — no refetch 0..N). */
    const contactsNextOffsetRef = useRef(0);
    const isLoadingRef = useRef(false); // Ref for synchronous locking
    const CONTACTS_PAGE_SIZE = 50;
    const visionEnabled = isMendsVisionEnabled();
    const [cmdOpen, setCmdOpen] = useState(false);
    const [register, setRegister] = useState<RegisterConversationOpenDetail | null>(null);
    useEffect(() => {
        return subscribeRegisterConversation((detail) => setRegister(detail));
    }, []);
    const openCmd = useCallback(() => setCmdOpen(true), []);
    useCommandPaletteHotkey(openCmd);
    // "Perguntar à MENDS" da Central abre a Barra (o contexto da pergunta fica com a própria Barra).
    useEffect(() => subscribeCentralQuestion(() => setCmdOpen(true)), []);

    /** Conteúdo da área principal, exceto o WhatsApp Chat (rota exata /chat) — esse fica montado sempre. */
    const renderMainPanelContent = () => {
        const path = location.pathname;
        const sectorId = selectedSectorId ? selectedSectorId.toString() : null;
        const componentKey = `${path}-${sectorId || 'no-sector'}`;

        if (path.includes('/google-ads-leads')) {
            return <div key={componentKey} className="page-container"><GoogleAdsLeadsPage /></div>;
        } else if (path.includes('/prazo-atendimento')) {
            return <div key={componentKey} className="page-container"><AttendancePage /></div>;
        } else if (path.includes('/sectors')) {
            return <div key={componentKey} className="page-container"><SectorsPage /></div>;
        } else if (path === '/' || path === '/dashboard') {
            return <DashBoard key={componentKey} />;
        } else if (path.includes('/email')) {
            return <div key={componentKey} className="page-container"><EmailPage /></div>;
        } else if (path === '/chat') {
            return null;
        } else if (path.includes('/follow-up')) {
            return <div key={componentKey} className="page-container"><FollowUpPage /></div>;
        } else if (path.includes('/agenda')) {
            return <div key={componentKey} className="page-container"><AgendaPage /></div>;
        } else if (path.includes('/schedule')) {
            return <MessageSchedule key={componentKey} />;
        } else if (path.startsWith('/crm/oportunidade/')) {
            // Ficha da oportunidade: página própria (antes do /crm, que mostra o Kanban).
            return modules.crm
                ? <div key={componentKey} className="page-container"><OpportunityDossierPage /></div>
                : <Navigate key={componentKey} to="/dashboard" replace />;
        } else if (path.includes('/crm')) {
            return <CRMPage key={componentKey} />;
        } else if (path.includes('/profile')) {
            return <div key={componentKey} className="page-container"><ProfilePage /></div>;
        } else if (path.includes('/labels')) {
            return <LabelPage key={componentKey} />;
        } else if (path.includes('/ai-agent')) {
            return <div key={componentKey} className="page-container"><AIAgentPage /></div>;
        } else if (path.includes('/groups')) {
            if (isOfficial) {
                return <Navigate key={componentKey} to="/chat" replace />;
            }
            return <div key={componentKey} className="page-container"><GroupsPage /></div>;
        } else if (path.includes('/contacts')) {
            return <div key={componentKey} className="page-container"><ContactsPage /></div>;
        } else if (path.includes('/broadcast')) {
            return <div key={componentKey} className="page-container"><BroadcastPage /></div>;
        } else if (visionEnabled && path === '/mente') {
            return <MindPage key={componentKey} />;
        } else if (visionEnabled && path.includes('/hoje')) {
            return <div key={componentKey} className="page-container"><TodayPage /></div>;
        } else if (visionEnabled && path.includes('/comecar')) {
            return <div key={componentKey} className="page-container"><OnboardingPage /></div>;
        } else if (visionEnabled && path.includes('/registrar')) {
            return <div key={componentKey} className="page-container"><RegisterPage /></div>;
        } else if (visionEnabled && path.includes('/oportunidades')) {
            return <div key={componentKey} className="page-container"><OpportunitiesPage /></div>;
        } else if (visionEnabled && path.includes('/aprovacoes')) {
            return <div key={componentKey} className="page-container"><ApprovalsPage /></div>;
        } else if (visionEnabled && path.includes('/central-inteligencia')) {
            return <div key={componentKey} className="page-container"><IntelligenceCenterPage /></div>;
        }
        return <DashBoard key={componentKey} />;
    };

    // Sync menu selection with URL path
    useEffect(() => {
        const path = location.pathname;
        let key = '1'; // Default to Dashboard

        if (path === '/mente') key = '25';
        else if (path.includes('/hoje')) key = '20';
        else if (path.includes('/registrar')) key = '28';
        else if (path.includes('/comecar')) key = '29';
        else if (path.includes('/central-inteligencia')) key = '23';
        else if (path.includes('/oportunidades')) key = '21';
        else if (path.includes('/aprovacoes')) key = '22';
        else if (path.includes('/follow-up')) key = '19';
        else if (path.includes('/chat')) key = '2';
        else if (path.includes('/email')) key = '15';
        else if (path.includes('/agenda')) key = '26';
        else if (path.includes('/groups')) key = '12';
        else if (path.includes('/schedule')) key = '3';
        else if (path.includes('/crm')) key = '5';
        else if (path.includes('/labels')) key = '8';
        else if (path.includes('/profile')) {
            if (path.includes('settings')) key = '6'; // Assuming settings parent
            else key = '7';
        }
        else if (path.includes('/google-ads-leads')) key = '27';
        else if (path.includes('/prazo-atendimento')) key = '30';
        else if (path.includes('/sectors')) key = '10';
        else if (path.includes('/ai-agent')) key = '11';
        else if (path.includes('/broadcast')) key = '14';
        else if (path.includes('/contacts')) key = '13';

        // Only update if different (avoid loops if any)
        setSelectedMenuKey(prev => prev !== key ? key : prev);
    }, [location.pathname]);

    // Listener para mudanças de status do WebSocket
    useEffect(() => {
        const handleWebSocketStatusChange = (event: CustomEvent) => {
            const { status } = event.detail;
            setWebsocketConnectionStatus(status);
        };

        // Definir status inicial
        setWebsocketConnectionStatus(ChatWebSocketService.connectionStatus);

        window.addEventListener('websocketStatusChange', handleWebSocketStatusChange as EventListener);
        return () => {
            window.removeEventListener('websocketStatusChange', handleWebSocketStatusChange as EventListener);
        };
    }, []);

    // Buscar status da conexão WhatsApp via API quando o setor muda
    // Quando desconectado/erro: verificar a cada 5s para o indicador voltar a verde assim que reconectar.
    // Quando conectado: verificar a cada 15s para detectar queda (antes era 30s).
    const WHATSAPP_POLL_CONNECTED_MS = 15000;
    const WHATSAPP_POLL_DISCONNECTED_MS = 5000;

    useEffect(() => {
        const fetchWhatsAppStatus = async () => {
            if (!selectedSectorId) {
                setWhatsappConnectionStatus('disconnected');
                return;
            }

            const sectorId = selectedSectorId;
            const sector = sectors.find(s => s.id === sectorId);

            if (!sector) {
                setWhatsappConnectionStatus('disconnected');
                return;
            }

            // Setor com WhatsApp oficial direto na Meta: a bolinha é o status real da conta.
            try {
                const meta = await getMetaConnectionStatus(sectorId);
                const cloud = cloudWhatsAppConnectionState(meta.accounts);
                if (cloud) {
                    setWhatsappConnectionStatus(cloud);
                    return;
                }
            } catch (error) {
                console.error('Error fetching WhatsApp oficial status:', error);
            }

            // Bolinha do menu = conexão do setor selecionado. A conta pode ter a linha anterior e QR ao mesmo tempo.
            if (sector.isOfficial) {
                setWhatsappConnectionStatus(sector.sparkIsActive === true ? 'connected' : 'disconnected');
                try {
                    const status = await getWhatsAppStatus(sectorId, true);
                    setWhatsappConnectionStatus(status.status);
                } catch (error) {
                    console.error('Error fetching WhatsApp oficial (linha anterior) status:', error);
                }
                return;
            }

            try {
                const status = await getWhatsAppStatus(sectorId, false);
                setWhatsappConnectionStatus(status.status);
            } catch (error) {
                console.error('Error fetching WhatsApp status:', error);
                setWhatsappConnectionStatus('error');
            }
        };

        fetchWhatsAppStatus();

        const intervalMs = whatsappConnectionStatus === 'connected' ? WHATSAPP_POLL_CONNECTED_MS : WHATSAPP_POLL_DISCONNECTED_MS;
        const interval = setInterval(fetchWhatsAppStatus, intervalMs);

        return () => clearInterval(interval);
    }, [selectedSectorId, sectors, whatsappConnectionStatus]);

    useEffect(() => {
        const checkAuthentication = () => {
            const tokenFromSession = SessionService.getToken();
            if (!tokenFromSession) {
                navigate('/');
                return false;
            }

            if (SessionService.isTokenExpired(tokenFromSession)) {
                SessionService.clearSession();
                navigate('/');
                return false;
            }

            return true;
        };

        checkAuthentication();
    }, [navigate]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth >= 768) {
                setDrawerVisible(false);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [setDrawerVisible]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const token = SessionService.getToken();
                const decodedToken = token ? SessionService.decodeToken(token) : null;
                const userId = decodedToken ? decodedToken.userId : null;

                if (!userId) return;

                // Verificar se é admin usando a função utilitária
                setIsAdmin(SessionService.isCurrentUserAdmin());

                const response = await fetch(`${config.API_BASE_URL}/users/profile`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const userData = await response.json();
                    if (userData?.data) {
                        setName(userData.data.name || '');
                        // Usar isAdmin do profile para definir o menu
                        if (typeof userData.data.isAdmin === 'boolean') {
                            setIsAdmin(userData.data.isAdmin);
                        }
                        if (userData.data.avatarUrl) {
                            const processedUrl = processAvatarUrl(userData.data.avatarUrl);
                            setAvatar(processedUrl);
                        } else {
                            setAvatar(null);
                        }
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar dados do usuário no menu:', error);
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        const handleProfileUpdate = (event: Event) => {
            const profileEvent = event as ProfileUpdateEvent;
            console.log('Profile update event received:', profileEvent.detail);

            const newAvatarUrl = profileEvent.detail.avatarUrl;
            const newName = profileEvent.detail.name;

            if (newName) {
                setName(newName);
            }

            if (newAvatarUrl) {
                const processedUrl = processAvatarUrl(newAvatarUrl);
                setAvatar(processedUrl);
            } else {
                setAvatar(null);
            }
        };

        window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);

        return () => {
            window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);
        };
    }, []);

    // Função para buscar contatos do setor selecionado (apenas ativos por padrão)
    // Uses optimized /contacts/counts endpoint for all counts in a single DB query
    const fetchContacts = useCallback(async (sectorId: string) => {
        if (!sectorId) return;
        try {
            contactsNextOffsetRef.current = 0;

            // Fetch active contacts list + all counts in parallel (2 requests, counts = 1 SQL query)
            const [response, counts] = await Promise.all([
                getContactsWithFilters(Number(sectorId), false, false, CONTACTS_PAGE_SIZE, 0, 'chat'),
                getContactCounts(Number(sectorId))
            ]);

            // Race condition check: ignore if sector changed
            if (Number(sectorId) !== Number(selectedSectorIdRef.current)) {
                console.log('[DEBUG] Ignoring stale fetch response for sector', sectorId);
                return;
            }

            // Process active contacts — merge 1ª página no estado existente para não apagar
            // scroll/páginas já carregadas no chat ao trocar de rota (ex.: /broadcast) ou a cada poll.
            if (response && response.data) {
                setContacts((prev) => mergeChatListFirstPageFromPoll(prev as any[], response.data));
                contactsNextOffsetRef.current =
                    response.pagination?.nextOffset ?? response.data.length;
                setTotalContacts(response.pagination?.total ?? response.data.length);
                const total = response.pagination?.total ?? 0;
                setHasMoreContacts(Boolean(response.pagination?.hasMore) || response.data.length < total);
            } else {
                setContacts([]);
                setTotalContacts(0);
                setHasMoreContacts(false);
            }

            // Set all counts from the optimized endpoint
            setUnreadCount(counts.unread);
            setFavoritesCount(counts.favorites);
            setArchivedCount(counts.archived);
            setWhatsappGroupsCount(counts.groups);

        } catch (error) {
            console.error('[DEBUG] Erro ao buscar contatos:', error);
            try {
                const fallbackResponse = await getContacts(Number(sectorId));
                if (fallbackResponse && fallbackResponse.data) {
                    const activeContacts = fallbackResponse.data.filter((contact: any) => !contact.archived);
                    setContacts(activeContacts);
                    setTotalContacts(activeContacts.length);
                    setHasMoreContacts(false);
                    setUnreadCount(activeContacts.filter((c: any) => !(c.isViewed ?? c.is_viewed ?? true)).length);
                    setFavoritesCount(activeContacts.filter((c: any) => c.isFavorite).length);
                    setArchivedCount(fallbackResponse.data.filter((contact: any) => contact.archived).length);
                } else {
                    setContacts([]);
                    setTotalContacts(0);
                    setHasMoreContacts(false);
                    setUnreadCount(0);
                    setFavoritesCount(0);
                    setArchivedCount(0);
                }
            } catch (fallbackError) {
                console.error('[DEBUG] Erro no fallback:', fallbackError);
                setContacts([]);
                setHasMoreContacts(false);
            }
        }
    }, []);

    // Load more contacts for infinite scroll
    const loadMoreContacts = useCallback(async () => {
        const currentSectorId = selectedSectorIdRef.current;
        // Check ref for immediate lock
        if (!currentSectorId || isLoadingRef.current || !hasMoreContacts) return;

        isLoadingRef.current = true;
        setIsLoadingMoreContacts(true);
        const offset = contactsNextOffsetRef.current;

        try {
            const response = await getContactsWithFilters(
                Number(currentSectorId), false, false, CONTACTS_PAGE_SIZE, offset, 'chat'
            );

            // Race condition check
            if (Number(currentSectorId) !== Number(selectedSectorIdRef.current)) {
                console.log('[DEBUG] Ignoring stale loadMore response');
                return;
            }

            if (response && response.data && response.data.length > 0) {
                contactsNextOffsetRef.current =
                    response.pagination?.nextOffset ?? offset + response.data.length;

                setContacts(prev =>
                    dedupeChatContactsForList([...prev, ...response.data] as any[])
                );

                if (response.pagination) {
                    setTotalContacts(response.pagination.total);
                    setHasMoreContacts(Boolean(response.pagination.hasMore));
                } else {
                    setHasMoreContacts(false);
                }

            } else {
                setHasMoreContacts(false);
            }
        } catch (error) {
            console.error('[DEBUG] Erro ao carregar mais contatos:', error);
        } finally {
            isLoadingRef.current = false;
            setIsLoadingMoreContacts(false);
        }
    }, [selectedSectorId, hasMoreContacts]); // Removed isLoadingMoreContacts dependency as we use ref

    const chatProps = {
        hasNewMessages: chatNewMessagesState,
        setHasNewMessages: setChatNewMessagesState,
        contacts,
        setContacts,
        loadMoreContacts,
        hasMoreContacts,
        isLoadingMoreContacts,
        totalContacts,
        archivedCount,
        favoritesCount,
        unreadCount,
        setUnreadCount,
        whatsappGroupsCount,
    };

    // Buscar contatos quando o setor mudar (somente na rota Chat)
    useEffect(() => {
        if (selectedSectorId && location.pathname === '/chat') {
            setContacts([]);
            setTotalContacts(0);
            setHasMoreContacts(false);
            setIsLoadingMoreContacts(false);
            isLoadingRef.current = false;
            contactsNextOffsetRef.current = 0;

            fetchContacts(selectedSectorId.toString());
        } else if (!selectedSectorId) {
            setContacts([]);
            setTotalContacts(0);
            setHasMoreContacts(false);
            setChatNewMessagesState({});
            setChatMenuHasNewActivity(false);
            setArchivedCount(0);
            setFavoritesCount(0);
            setUnreadCount(0);
            setWhatsappGroupsCount(0);
        }
    }, [selectedSectorId, fetchContacts, location.pathname]);

    // ── Polling: refresh contacts every 5 seconds (only on Chat — avoids load on other pages) ──
    useEffect(() => {
        if (!selectedSectorId || location.pathname !== '/chat') return;

        const pollContacts = () => {
            fetchContacts(selectedSectorId.toString());
        };

        const intervalId = setInterval(pollContacts, CONTACTS_POLL_INTERVAL);
        return () => clearInterval(intervalId);
    }, [selectedSectorId, fetchContacts, location.pathname]);

    // ── Notification polling: delegate to NotificationService ──
    useEffect(() => {
        if (!selectedSectorId) return;
        notificationService.startPolling(Number(selectedSectorId));
        return () => notificationService.stopPolling();
    }, [selectedSectorId]);

    // Sidebar Chat dot when away from /chat — driven by unread notifications (new incoming messages)
    useEffect(() => {
        const syncChatMenuIndicator = (notifications: Notification[]) => {
            if (pathnameRef.current === '/chat') {
                setChatMenuHasNewActivity(false);
                return;
            }
            setChatMenuHasNewActivity(notifications.some((n) => !n.isRead));
        };

        syncChatMenuIndicator(notificationService.getAll());

        const handleNotificationUpdate = (event: Event) => {
            const detail = (event as CustomEvent<{ notifications: Notification[] }>).detail;
            if (detail?.notifications) {
                syncChatMenuIndicator(detail.notifications);
            }
        };

        window.addEventListener('notificationUpdate', handleNotificationUpdate);
        return () => window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    }, []);

    useEffect(() => {
        if (location.pathname === '/chat') {
            setChatMenuHasNewActivity(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        setChatMenuHasNewActivity(false);
    }, [selectedSectorId]);

    // ── WebSocket: chat em tempo real (Spark oficial e Uazapi) ──
    useEffect(() => {
        const token = SessionService.getToken();
        if (!selectedSectorId || !token) {
            ChatWebSocketService.disconnect();
            return;
        }

        ChatWebSocketService.connect(token, selectedSectorId).catch((err) => {
            console.error('[CombinedMenu] WebSocket connect failed:', err);
        });

        const handleIncomingActivity = (data: Record<string, unknown>) => {
            const isSent = Boolean(data?.isSent ?? data?.is_sent ?? false);
            if (isSent) return;

            if (pathnameRef.current === '/chat') {
                if (selectedSectorIdRef.current) {
                    fetchContacts(selectedSectorIdRef.current.toString());
                }
                return;
            }

            setChatMenuHasNewActivity(true);
        };

        const refreshContactsOnChat = () => {
            if (pathnameRef.current !== '/chat' || !selectedSectorIdRef.current) return;
            fetchContacts(selectedSectorIdRef.current.toString());
        };

        const handleHumanTransfer = () => {
            setChatMenuHasNewActivity(true);
            void notificationService.refreshNow();
            refreshContactsOnChat();
        };

        const unsubMessage = ChatWebSocketService.on('message', handleIncomingActivity);
        const unsubGroup = ChatWebSocketService.on('group_message', handleIncomingActivity);
        const unsubContact = ChatWebSocketService.on('contact', refreshContactsOnChat);
        const unsubAi = ChatWebSocketService.on('contact_ai_status', refreshContactsOnChat);
        const unsubTransfer = ChatWebSocketService.on('human_transfer_notification', handleHumanTransfer);

        return () => {
            unsubMessage();
            unsubGroup();
            unsubContact();
            unsubAi();
            unsubTransfer();
            ChatWebSocketService.disconnect();
        };
    }, [selectedSectorId, sectors, fetchContacts]);

    useEffect(() => {
        const handleWhatsAppConnected = () => {
            if (selectedSectorId && location.pathname === '/chat') {
                fetchContacts(selectedSectorId.toString());
            }
        };

        const handleReloadActiveContacts = (event: CustomEvent) => {
            const { sectorId } = event.detail;
            if (
                location.pathname === '/chat' &&
                sectorId &&
                sectorId === (selectedSectorId ? selectedSectorId.toString() : '')
            ) {
                fetchContacts(selectedSectorId!.toString());
            }
        };

        window.addEventListener('whatsappConnected', handleWhatsAppConnected);
        window.addEventListener('reloadActiveContacts', handleReloadActiveContacts as EventListener);

        return () => {
            window.removeEventListener('whatsappConnected', handleWhatsAppConnected);
            window.removeEventListener('reloadActiveContacts', handleReloadActiveContacts as EventListener);
        };
    }, [selectedSectorId, fetchContacts, location.pathname]);

    const selectedConnectionTitle = useMemo(() => {
        if (!selectedSectorId || !sectors?.length) return '';
        return sectors.find((s) => s.id === selectedSectorId)?.name ?? '';
    }, [selectedSectorId, sectors]);

    const handleSectorChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;

        try {
            if (value) {
                const sectorId = parseInt(value, 10);
                if (!Number.isFinite(sectorId) || sectorId <= 0) {
                    selectSector(null);
                    return;
                }
                if (sectorId !== selectedSectorId) {
                    selectSector(sectorId);
                }
            } else {
                selectSector(null);
            }
        } catch (error) {
            console.error('Erro ao mudar setor:', error);
        }
    };

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    const toggleMobileMenu = () => {
        setDrawerVisible(!drawerVisible);
    };

    const toggleUserDropdown = () => {
        setShowUserDropdown(!showUserDropdown);
    };

    /** O que sai da navegação principal (Vision Deck) e continua acessível em Configurações. */
    const visionSettingsItems = (): { key: string; label: string }[] => [
        { key: '1', label: 'Painel' },
        ...(modules.email ? [{ key: '15', label: 'E-mail' }] : []),
        ...(!isOfficial ? [{ key: '12', label: 'Grupos' }] : []),
        ...(modules.campaign ? [{ key: '14', label: 'Campanhas' }] : []),
        ...(modules.schedule ? [{ key: '3', label: 'Agendamentos' }] : []),
        ...(modules.calendar ? [{ key: '26', label: 'Agenda' }] : []),
        { key: '22', label: 'Autonomia da IA' },
        { key: '30', label: 'Prazo e roteamento' },
        { key: '28', label: 'Histórico do Registrar' },
        { key: '29', label: 'Começar (conexões e base)' },
        ...(isAdmin ? [{ key: '10', label: 'Conexões' }, { key: '27', label: 'Formulários' }] : []),
    ];

    // Ao abrir uma página de Configurações (link, voltar do navegador), o grupo aparece aberto.
    useEffect(() => {
        if (!visionEnabled) return;
        if (!SETTINGS_CHILD_KEYS.has(selectedMenuKey)) return;
        setExpandedSubmenus(prev => (prev.includes('6') ? prev : [...prev, '6']));
    }, [selectedMenuKey, visionEnabled]);

    const toggleSubmenu = (key: string) => {
        if (expandedSubmenus.includes(key)) {
            setExpandedSubmenus(expandedSubmenus.filter(item => item !== key));
        } else {
            setExpandedSubmenus([...expandedSubmenus, key]);
        }
    };

    const handleLogout = () => {
        clearAppCache();
        SessionService.clearSession();
        navigate('/');
    };

    const getPathFromKey = (key: string): string => {
        switch (key) {
            case '1': return '/dashboard';
            case '2': return '/chat';
            case '15': return '/email';
            case '26': return '/agenda';
            case '3': return '/schedule';
            case '5': return '/crm';
            case '7': return '/profile';
            case '8': return '/labels';
            case '10': return '/sectors';
            case '27': return '/google-ads-leads';
            case '11': return '/ai-agent';
            case '12': return '/groups';
            case '13': return '/contacts';
            case '14': return '/broadcast';
            case '19': return '/follow-up';
            case '20': return '/hoje';
            case '21': return '/oportunidades';
            case '22': return '/aprovacoes';
            case '23': return '/central-inteligencia';
            case '25': return '/mente';
            case '28': return '/registrar';
            case '29': return '/comecar';
            case '30': return '/prazo-atendimento';
            default: return '/dashboard';
        }
    };

    const handleMenuClick = (key: string) => {
        if (key === '6' || key === '24') {
            toggleSubmenu(key);
            return;
        }

        setSelectedMenuKey(key);
        localStorage.setItem('selectedMenuKey', key);

        if (key === '2') {
            setChatMenuHasNewActivity(false);
            if (selectedSectorId) {
                fetchContacts(selectedSectorId.toString());
            }
        }

        const path = getPathFromKey(key);

        if (key === '11') void refreshModules();

        if (location.pathname !== path) {
            navigate(path);
        }

        if (isMobile) {
            setDrawerVisible(false);
        }

        setShowUserDropdown(false);
    };

    const handleSubmenuClick = (parentKey: string, childKey: string) => {
        navigationInProgressRef.current = true;
        setSelectedMenuKey(childKey);
        localStorage.setItem('selectedMenuKey', childKey);

        const path = getPathFromKey(childKey);
        if (childKey === '11') void refreshModules();
        navigate(path);

        if (isMobile) {
            setDrawerVisible(false);
        }
    };



    // Função para renderizar indicador de status de conexão
    const renderConnectionStatusIndicator = () => {
        const getStatusColor = () => {
            return whatsappConnectionStatus === 'connected' ? 'var(--success-color)' : 'var(--error-color)';
        };

        const getStatusTitle = () => {
            return whatsappConnectionStatus === 'connected'
                ? 'WhatsApp conectado ✓'
                : 'WhatsApp desconectado ✗';
        };

        return (
            <div
                className="connection-status-indicator"
                title={getStatusTitle()}
                style={{
                    backgroundColor: getStatusColor(),
                    cursor: 'help'
                }}
            />
        );
    };

    const renderDestination = (key: string, label: string, icon: React.ReactNode, activeKeys: string[] = [key], badge?: React.ReactNode) => (
        <li key={key} className={`menu-item ${activeKeys.includes(selectedMenuKey) ? 'active' : ''}`}>
            <div className="menu-item-content" onClick={() => handleMenuClick(key)}>
                <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {icon}
                </span>
                <span className="menu-label"><span>{label}</span></span>
                {badge}
                {collapsed && <div className="menu-tooltip">{label}</div>}
            </div>
        </li>
    );

    /** Vision Deck: seis destinos na navegação; o resto do produto fica dentro de Configurações. */
    const renderDestinationMenu = () => {
        const settingsItems = visionSettingsItems();
        return (
            <ul className="menu-items">
                {renderDestination('20', 'Hoje', <FiHome size={18} />)}
                {renderDestination(
                    '2',
                    'Conversas',
                    <FiMessageSquare size={18} />,
                    ['2'],
                    location.pathname !== '/chat' && chatMenuHasNewActivity
                        ? <div className="notification-dot" title="Nova mensagem" />
                        : undefined,
                )}
                {renderDestination('13', 'Clientes', <FiUser size={18} />)}
                {modules.crm && renderDestination('21', 'Oportunidades', <FiTrendingUp size={18} />, ['21', '5'])}
                {renderDestination('11', 'Funcionários', <FiBriefcase size={18} />)}
                {renderDestination('23', 'Central de IA', <FiCpu size={18} />)}
                <li className={`menu-item menu-item-settings ${expandedSubmenus.includes('6') ? 'expanded' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('6')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiSettings size={18} />
                        </span>
                        <span className="menu-label"><span>Configurações</span></span>
                        <span className="menu-submenu-arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M7 10l5 5 5-5z" />
                            </svg>
                        </span>
                        {collapsed && <div className="menu-tooltip">Configurações</div>}
                    </div>
                    <ul className="menu-submenu">
                        {settingsItems.map((item) => (
                            <li
                                key={item.key}
                                className={`menu-submenu-item ${selectedMenuKey === item.key ? 'active' : ''}`}
                                onClick={() => handleSubmenuClick('6', item.key)}
                            >
                                <span className="submenu-dot"></span>
                                <span className="submenu-label"><span>{item.label}</span></span>
                            </li>
                        ))}
                    </ul>
                </li>
            </ul>
        );
    };

    const renderMenuItems = () => {
        if (visionEnabled) return renderDestinationMenu();
        return (
            <ul className="menu-items">
                <li className={`menu-item ${selectedMenuKey === '1' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('1')}>
                        <span className="menu-icon icon-dashboard"></span>
                        <span className="menu-label"><span>Dashboard</span></span>
                        {collapsed && <div className="menu-tooltip">Dashboard</div>}
                    </div>
                </li>
                <li className={`menu-item ${selectedMenuKey === '2' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('2')}>
                        <span className="menu-icon icon-message"></span>
                        <span className="menu-label"><span>Conversas</span></span>
                        {location.pathname !== '/chat' && chatMenuHasNewActivity && (
                            <div className="notification-dot" title="Nova mensagem" />
                        )}
                        {collapsed && <div className="menu-tooltip">Conversas</div>}
                    </div>
                </li>
                {modules.email && (
                <li className={`menu-item ${selectedMenuKey === '15' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('15')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiMail size={18} />
                        </span>
                        <span className="menu-label"><span>Email</span></span>
                        {collapsed && <div className="menu-tooltip">Email</div>}
                    </div>
                </li>
                )}
                {!isOfficial && (
                <li className={`menu-item ${selectedMenuKey === '12' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('12')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiUsers size={18} />
                        </span>
                        <span className="menu-label"><span>Grupos</span></span>
                        {collapsed && <div className="menu-tooltip">Grupos</div>}
                    </div>
                </li>
                )}
                <li className={`menu-item ${selectedMenuKey === '13' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('13')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiUser size={18} />
                        </span>
                        <span className="menu-label"><span>{visionEnabled ? 'Clientes' : 'Contatos'}</span></span>
                        {collapsed && <div className="menu-tooltip">{visionEnabled ? 'Clientes' : 'Contatos'}</div>}
                    </div>
                </li>
                {modules.campaign && (
                <li className={`menu-item ${selectedMenuKey === '14' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('14')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiTarget size={18} />
                        </span>
                        <span className="menu-label"><span>Campanhas</span></span>
                        {collapsed && <div className="menu-tooltip">Campanhas</div>}
                    </div>
                </li>
                )}
                {modules.schedule && (
                <li className={`menu-item ${selectedMenuKey === '3' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('3')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiCalendar size={18} />
                        </span>
                        <span className="menu-label"><span>Agendamentos</span></span>
                        {collapsed && <div className="menu-tooltip">Agendamentos</div>}
                    </div>
                </li>
                )}
                {modules.calendar && (
                <li className={`menu-item ${selectedMenuKey === '26' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('26')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiCalendar size={18} />
                        </span>
                        <span className="menu-label"><span>Agenda</span></span>
                        {collapsed && <div className="menu-tooltip">Agenda</div>}
                    </div>
                </li>
                )}
                {modules.crm && (
                <li className={`menu-item ${selectedMenuKey === '5' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('5')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiLayers size={18} />
                        </span>
                        <span className="menu-label"><span>CRM</span></span>
                        {collapsed && <div className="menu-tooltip">CRM</div>}
                    </div>
                </li>
                )}
                {SHOW_LABELS_MENU && (
                <li className={`menu-item ${selectedMenuKey === '8' ? 'active' : ''}`}>
                    <div className="menu-item-content" onClick={() => handleMenuClick('8')}>
                        <span className="menu-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiTag size={18} />
                        </span>
                        <span className="menu-label"><span>Labels</span></span>
                        {collapsed && <div className="menu-tooltip">Labels</div>}
                    </div>
                </li>
                )}
                {isAdmin && (
                    <li className={`menu-item ${expandedSubmenus.includes('6') ? 'expanded' : ''}`}>
                        <div className="menu-item-content" onClick={() => handleMenuClick('6')}>
                            <span className="menu-icon icon-settings"></span>
                            <span className="menu-label"><span>Configurações</span></span>
                            <span className="menu-submenu-arrow">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                                </svg>
                            </span>
                            {collapsed && <div className="menu-tooltip">Configurações</div>}
                        </div>
                        <ul className="menu-submenu">
                            <li
                                className={`menu-submenu-item ${selectedMenuKey === '10' ? 'active' : ''}`}
                                onClick={() => handleSubmenuClick('6', '10')}
                            >
                                <span className="submenu-dot"></span>
                                <span className="submenu-label"><span>Conexões</span></span>
                            </li>
                            <li
                                className={`menu-submenu-item ${selectedMenuKey === '27' ? 'active' : ''}`}
                                onClick={() => handleSubmenuClick('6', '27')}
                            >
                                <span className="submenu-dot"></span>
                                <span className="submenu-label"><span>Google Ads</span></span>
                            </li>
                            <li
                                className={`menu-submenu-item ${selectedMenuKey === '30' ? 'active' : ''}`}
                                onClick={() => handleSubmenuClick('6', '30')}
                            >
                                <span className="submenu-dot"></span>
                                <span className="submenu-label"><span>Prazo e roteamento</span></span>
                            </li>
                            {!visionEnabled && (
                            <li
                                className={`menu-submenu-item ${selectedMenuKey === '11' ? 'active' : ''}`}
                                onClick={() => handleSubmenuClick('6', '11')}
                            >
                                <span className="submenu-dot"></span>
                                <span className="submenu-label"><span>Funcionários</span></span>
                            </li>
                            )}
                        </ul>
                    </li>
                )}
            </ul>
        );
    };

    return (
        <div className={`combined-menu-wrapper ${visionEnabled ? 'mai-shell-light' : ''}`}>
            <div className="menu-layout">
                <header className="menu-header">
                    <div className="menu-header-left">
                        <button className="menu-toggle-button" onClick={isMobile ? toggleMobileMenu : toggleSidebar}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path fill="currentColor" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                            </svg>
                        </button>
                        <div className="menu-logo-container">
                            <img src={logo} alt="Logo" className="menu-logo" />
                        </div>
                    </div>
                    <div className="menu-header-right">
                        {visionEnabled && (
                            <>
                                <button
                                    type="button"
                                    className="mai-header-register"
                                    onClick={() => openRegisterConversation({ sourceSurface: 'global', screen: 'unknown' })}
                                    title="Registrar — acontecimento do mundo real"
                                >
                                    <FiMic size={16} />
                                    <span className="mai-header-register-label">Registrar</span>
                                </button>
                                <button
                                    type="button"
                                    className="mai-cmd-trigger"
                                    onClick={() => setCmdOpen(true)}
                                    title="Barra da IA (⌘K)"
                                >
                                    <span>Pergunte à MENDS…</span>
                                    <span className="mai-cmd-kbd">⌘K</span>
                                </button>
                            </>
                        )}
                        <NotificationBell />
                        <div className="menu-user-profile" onClick={toggleUserDropdown} ref={userDropdownRef}>
                            {avatar ? (
                                <img
                                    src={avatar}
                                    alt={name}
                                    className="menu-avatar"
                                    key={avatar}
                                    crossOrigin="anonymous"
                                    onLoad={() => {
                                        // Avatar carregado
                                    }}
                                    onError={(e) => {
                                        console.error('Erro ao carregar avatar no menu:', {
                                            src: avatar,
                                            error: e
                                        });

                                        const target = e.target as HTMLImageElement;
                                        target.onerror = null;

                                        // Se for uma URL do Supabase, tentar recarregar uma vez
                                        if (avatar.includes('supabase.co') && !avatar.includes('retry=1')) {
                                            const retryUrl = avatar.includes('?')
                                                ? `${avatar}&retry=1`
                                                : `${avatar}?retry=1`;
                                            target.src = retryUrl;
                                        } else {
                                            setAvatar(null);
                                        }
                                    }}
                                />
                            ) : (
                                <span className="menu-avatar-placeholder" aria-hidden>{userInitials(name)}</span>
                            )}
                            <span className="menu-username"><span>{name}</span></span>
                            <span className="menu-dropdown-arrow">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                                </svg>
                            </span>
                            {showUserDropdown && (
                                <div className="menu-user-dropdown">
                                    <div className="menu-dropdown-item" onClick={() => handleMenuClick('7')}>
                                        <span className="menu-dropdown-icon icon-user"></span>
                                        <span>Perfil</span>
                                    </div>
                                    <div className="menu-dropdown-item" onClick={handleLogout}>
                                        <span className="menu-dropdown-icon icon-logout"></span>
                                        <span>Sair</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className="menu-content-layout">
                    <aside className={`menu-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${drawerVisible && isMobile ? 'visible' : ''}`}>
                        <div className="menu-sidebar-content">
                            {isSectorsLoading ? (
                                <div className="menu-loading">
                                    <LoadingSpinner size="md" color="primary" />
                                    <span className="loading-text"><span>Carregando menu...</span></span>
                                </div>
                            ) : (
                                <>
                                    {renderMenuItems()}
                                    <div className="menu-sector-selector">
                                        <div className="sector-selector-container">
                                            <select
                                                value={selectedSectorId?.toString() || ''}
                                                onChange={handleSectorChange}
                                                className="menu-select"
                                                title={selectedConnectionTitle || 'Selecionar conexão'}
                                            >
                                                <option value="">Selecionar conexão</option>
                                                {(sectors || []).map((sector) => (
                                                    <option key={sector.id} value={sector.id} title={sector.name}>
                                                        {truncateConnectionLabel(sector.name)}
                                                    </option>
                                                ))}
                                            </select>
                                            {selectedSectorId && renderConnectionStatusIndicator()}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </aside>

                    <main
                        className="menu-content"
                        style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                    >
                        {/*
                          WhatsApp Chat permanece montado ao navegar (ex. /broadcast) para não perder lista/mensagens.
                          Fora de /chat fica invisível e sem interação; em /chat ocupa a área inteira.
                        */}
                        <div
                            className="combined-menu-persistent-chat-host"
                            style={
                                location.pathname === '/chat'
                                    ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                                    : {
                                          position: 'absolute',
                                          width: 1,
                                          height: 1,
                                          padding: 0,
                                          margin: -1,
                                          overflow: 'hidden',
                                          clip: 'rect(0, 0, 0, 0)',
                                          whiteSpace: 'nowrap',
                                          border: 0,
                                          opacity: 0,
                                          pointerEvents: 'none',
                                          zIndex: -1,
                                      }
                            }
                            aria-hidden={location.pathname !== '/chat'}
                        >
                            <Chat
                                key={`persistent-whatsapp-chat-${selectedSectorId ?? 'none'}`}
                                {...chatProps}
                            />
                        </div>
                        {location.pathname !== '/chat' && (
                            <div
                                className="combined-menu-main-panel"
                                style={
                                    location.pathname === '/mente'
                                        ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                                        : { flex: 1, minHeight: 0, overflow: 'auto' }
                                }
                            >
                                <ErrorBoundary key={`${location.pathname}-${selectedSectorId ?? 'no-sector'}`}>
                                    {renderMainPanelContent()}
                                </ErrorBoundary>
                            </div>
                        )}
                    </main>
                </div>

                {isMobile && drawerVisible && (
                    <div className="menu-overlay" onClick={toggleMobileMenu}></div>
                )}

                {visionEnabled && (
                    <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
                )}
                {visionEnabled && (
                    <RegisterConversationModal
                        open={register !== null}
                        defaultContactId={register?.contactId}
                        defaultContactName={register?.contactName}
                        sourceSurface={register?.sourceSurface}
                        opportunityId={register?.opportunityId}
                        screenContext={register ? {
                            screen: register.screen,
                            pathname: register.pathname,
                            conversationId: register.conversationId,
                            messageId: register.messageId,
                            calendarEventId: register.calendarEventId,
                            subjectKind: register.subjectKind,
                            contactId: register.contactId,
                            contactName: register.contactName,
                        } : undefined}
                        onClose={() => setRegister(null)}
                    />
                )}
                {/* Sem o botão flutuante onde ele duplica o Registrar ou cobre a barra de salvar da página. */}
                {visionEnabled && register === null
                    && !location.pathname.startsWith('/registrar')
                    && !location.pathname.startsWith('/follow-up') && (
                    <RegisterFab onOpen={() => openRegisterConversation({ sourceSurface: 'global', screen: 'unknown' })} />
                )}
                {visionEnabled && isMobile && (
                    <nav className="mai-bottom-nav" aria-label="Navegação MENDS">
                        <button
                            type="button"
                            className={location.pathname.includes('/hoje') ? 'is-active' : ''}
                            onClick={() => navigate('/hoje')}
                        >
                            <FiHome size={18} />
                            <span>Hoje</span>
                        </button>
                        <button
                            type="button"
                            className={location.pathname === '/chat' ? 'is-active' : ''}
                            onClick={() => handleMenuClick('2')}
                        >
                            <FiMessageSquare size={18} />
                            <span>Conversas</span>
                        </button>
                        <button
                            type="button"
                            className="mai-bottom-register"
                            onClick={() => navigate('/registrar')}
                            title="Registrar"
                            aria-label="Registrar"
                        >
                            <FiMic size={22} />
                            <span>Registrar</span>
                        </button>
                        <button
                            type="button"
                            className={location.pathname.includes('/contacts') ? 'is-active' : ''}
                            onClick={() => handleMenuClick('13')}
                        >
                            <FiUser size={18} />
                            <span>Clientes</span>
                        </button>
                        <button
                            type="button"
                            className={location.pathname.includes('/central-inteligencia') ? 'is-active' : ''}
                            onClick={() => handleMenuClick('23')}
                        >
                            <FiCpu size={18} />
                            <span>Central</span>
                        </button>
                    </nav>
                )}
            </div>
        </div>
    );
};

export default CombinedMenu;
